import { IFormJBPHPContactChangeRequest } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { useHpContacts } from 'legacy/lut/components/widgets/public/partner360/hooks/hpContacts/useHpContacts'
import { requestWizardSteps } from 'legacy/lut/components/widgets/public/partner360/planningWizard/features/planningWizard'
import { selectCommitmentItemsLoadingStatus } from 'legacy/lut/features/partner360/commitment'
import {
  selectFinancialYear,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Logic, Operator } from 'core-components/types'
import { MeasureController } from 'core/analytics'
import { useSalesTeamRoles } from 'partner360/features/hpContacts/model'
import { useHpContactsRoles } from 'partner360/features/hpContacts/model/useHpContactsRoles'
import { useLeadPbmId } from 'partner360/features/hpContacts/model/useLeadPbmId'
import { RamManagerAssignmentWelcome } from 'partner360/widgets/ramManagerAssignment/components/rolesAssignment/components/ramManagerAssignmentWelcome/RamManagerAssignmentWelcome'
import { RolesAssignment } from 'partner360/widgets/ramManagerAssignment/components/rolesAssignment/RolesAssignment'

import styles from './RamManagerAssignment.module.scss'

interface IRamManagerAssignmentProps {
  partnerId: string
  metrics: MeasureController
}

const CRUD_KEY = 'RamManagerAssignment'

export const RamManagerAssignment = ({ metrics, partnerId }: IRamManagerAssignmentProps) => {
  const {
    isLoading: isRolesLoading,
    partnerRoles,
    fetchPartnerRoles,
  } = useHpContactsRoles({
    partnerId,
    metrics,
  })

  const programType = useSelector(selectProgramType)
  const fYear = useSelector(selectFinancialYear)
  const commitmentItemsStatus = useSelector(selectCommitmentItemsLoadingStatus)

  const [isLoading, setIsLoading] = useState(true)
  const [contacts, setContacts] = useState<IFormJBPHPContactChangeRequest[]>([])
  const [isFirstFetching, setIsFirstFetching] = useState(true)
  const allRolesRaw = useSalesTeamRoles(metrics)
  const allRoles = useMemo(() => allRolesRaw, [allRolesRaw])
  const [leadPbmId] = useLeadPbmId({
    partnerId,
  })
  const dispatch = useAppDispatch()
  const { jbpHpContactChangeRequestCrud } = useHpContacts({
    crudKey: CRUD_KEY,
    partnerId,
  })

  const onUpdateWizard = useCallback(() => {
    dispatch(
      requestWizardSteps({
        partnerId,
        fYear,
        programType,
      })
    )
  }, [dispatch, fYear, partnerId, programType])

  const fetch = useCallback(async () => {
    setIsLoading(true)
    jbpHpContactChangeRequestCrud.setPaging({ page: 0, pageSize: 100 })
    jbpHpContactChangeRequestCrud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'PartnerId',
          operator: Operator.EQUAL,
          value: partnerId,
        },
      ],
    })
    const res = await jbpHpContactChangeRequestCrud.fetch()
    const sorted = [...res.items].sort(
      (a: IFormJBPHPContactChangeRequest, b: IFormJBPHPContactChangeRequest) =>
        (a.SortOrder ?? Number.MIN_SAFE_INTEGER) - (b.SortOrder ?? Number.MIN_SAFE_INTEGER)
    )
    setContacts(sorted)
    setIsLoading(false)
    setIsFirstFetching(false)
  }, [jbpHpContactChangeRequestCrud, partnerId])

  useEffect(() => {
    fetch()

    return jbpHpContactChangeRequestCrud.subscribe('change', () => {
      fetch()
    })
  }, [fetch, jbpHpContactChangeRequestCrud, partnerId])

  if (
    isFirstFetching &&
    (isLoading || isRolesLoading || commitmentItemsStatus === RequestStatus.PENDING)
  ) {
    return <Loader />
  }

  return (
    <div className={styles.root}>
      <LoaderOverlay
        show={isLoading || isRolesLoading || commitmentItemsStatus === RequestStatus.PENDING}
      />
      <RamManagerAssignmentWelcome contacts={contacts} />
      <RolesAssignment
        partnerId={partnerId}
        allRoles={allRoles}
        leadPbmId={leadPbmId}
        contacts={contacts}
        roles={partnerRoles}
        crudKey={CRUD_KEY}
        fetchPartnerRoles={fetchPartnerRoles}
        programType={programType}
        fYear={fYear}
        onUpdateWizard={onUpdateWizard}
        setIsLoading={setIsLoading}
      />
    </div>
  )
}
